import { useThree } from "@react-three/fiber";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three";

let activeDraggable: THREE.Object3D | null = null;
const draggableRoots = new WeakSet<THREE.Object3D>();
type DragCandidate = {
  point: THREE.Vector3;
  root: THREE.Object3D;
} | null;

const pointerDownCandidates = new WeakMap<PointerEvent, DragCandidate>();

const getDragCandidate = (
  event: PointerEvent,
  gl: THREE.WebGLRenderer,
  raycaster: THREE.Raycaster,
  camera: THREE.Camera,
  scene: THREE.Scene
): DragCandidate => {
  if (pointerDownCandidates.has(event)) {
    return pointerDownCandidates.get(event) ?? null;
  }

  const rect = gl.domElement.getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

  raycaster.setFromCamera({ x, y } as THREE.Vector2, camera);

  const hits = raycaster.intersectObjects(scene.children, true);
  for (const hit of hits) {
    let obj: THREE.Object3D | null = hit.object;
    while (obj) {
      if (draggableRoots.has(obj)) {
        const candidate = {
          point: hit.point.clone(),
          root: obj,
        };
        pointerDownCandidates.set(event, candidate);
        return candidate;
      }
      obj = obj.parent;
    }
  }

  pointerDownCandidates.set(event, null);
  return null;
};

const useDraggableMesh = (
  ref: RefObject<THREE.Object3D>,
  constraint: ((v: THREE.Vector3) => THREE.Vector3) | null,
  onDrag: (pos: THREE.Vector3) => void,
  enabled = true,
  updatePosition = true,
  dragLayer: number | null = null,
  onDragEnd?: (finalPos: THREE.Vector3) => void 
): { isDragging: boolean } => {
  const { camera, raycaster, gl, scene, invalidate } = useThree();
  const [isDragging, setIsDragging] = useState(false);

  const plane = useRef(new THREE.Plane());
  const offset = useRef(new THREE.Vector3());
  const startPos = useRef(new THREE.Vector3());
  const finalPos = useRef(new THREE.Vector3()); 

  useEffect(() => {
    const current = ref.current;
    if (current) draggableRoots.add(current);
    return () => {
      if (current) draggableRoots.delete(current);
    };
  }, [ref]);

  const startDrag = useCallback((point: THREE.Vector3) => {
    if (!ref.current || !enabled) return;
    activeDraggable = ref.current;
    setIsDragging(true);

    startPos.current.copy(ref.current.position);
    
    // --- FIX: Initialize finalPos to current position ---
    // This ensures that if 'move' never fires (a click), 
    // we return the original position, not 0,0,0
    finalPos.current.copy(ref.current.position); 
    // ----------------------------------------------------

    plane.current.setFromNormalAndCoplanarPoint(
      camera.getWorldDirection(new THREE.Vector3()).negate(),
      point
    );

    offset.current.copy(point).sub(ref.current.position);

    if (dragLayer !== null) ref.current!.layers.set(dragLayer);
    invalidate();
  }, [camera, enabled, ref, dragLayer, invalidate]);

  const stopDrag = useCallback(() => {
    if (activeDraggable === ref.current) {
      activeDraggable = null;
      if (dragLayer !== null && ref.current) ref.current.layers.set(0);
      
      // Trigger the callback with the final position
      if (onDragEnd) {
        // Optional Safety: Ensure we never send exactly 0,0,0 if it wasn't intentional
        // (Though the initialization fix above usually prevents this)
        onDragEnd(finalPos.current);
      }
      invalidate();
    }
    setIsDragging(false);
  }, [ref, dragLayer, onDragEnd, invalidate]);

  const move = useCallback((e: PointerEvent) => {
    if (!isDragging || activeDraggable !== ref.current) return;

    const rect = gl.domElement.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera({ x, y } as THREE.Vector2, camera);

    const intersect = new THREE.Vector3();
    if (raycaster.ray.intersectPlane(plane.current, intersect)) {
      const pos = intersect.sub(offset.current);
      const calculatedPos = constraint ? constraint(pos) : pos;

      if (updatePosition && ref.current) ref.current.position.copy(calculatedPos);
      
      // Update finalPos during drag
      finalPos.current.copy(calculatedPos);

      onDrag?.(calculatedPos);
      invalidate();
    }
  }, [isDragging, camera, gl, raycaster, constraint, updatePosition, onDrag, ref, invalidate]);

  useEffect(() => {
    if (dragLayer !== null) {
      camera.layers.enable(0);
      camera.layers.enable(dragLayer);
    }

    const down = (e: PointerEvent) => {
      if (!enabled || e.button !== 0) return;

      const candidate = getDragCandidate(e, gl, raycaster, camera, scene);
      if (candidate?.root === ref.current) {
        startDrag(candidate.point);
      }
    };

    gl.domElement.addEventListener("pointerdown", down);
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", stopDrag);
    document.addEventListener("pointercancel", stopDrag);

    return () => {
      gl.domElement.removeEventListener("pointerdown", down);
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", stopDrag);
      document.removeEventListener("pointercancel", stopDrag);
    };
  }, [gl, gl.domElement, camera, scene, raycaster, enabled, ref, startDrag, move, stopDrag, dragLayer]);

  return { isDragging };
};

export default useDraggableMesh;
